import * as React from "react";
import { AlertTriangle, ChevronRight, Cloud, Hourglass, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { colorVariantClasses } from "@/theme/AppColorVariant";
import MigrationStatusChip from "../../MigrationStatusChip";
import { ActionSelectItem } from "./ActionSelectItem";
import {
  ConflictGroupDto,
  ConflictGroupFileDto,
  ConflictWaitingForStateColors,
  conflictWaitingMessage,
} from "@/models/ConflictGroup";
import {
  ConflictReason,
  ConflictReasonColors,
  ConflictReasonDescriptions,
  ConflictReasonIcons,
  ConflictReasonLabels,
} from "@/models/ConflictReason";
import {
  MigrationFlagStatus,
  ConflictStatusColors,
  ConflictStatusDescriptions,
  ConflictStatusLabels,
} from "@/models/Flags/MigrationFlagStatus";
import {
  ConflictResolutionAction,
  ConflictResolutionActionColors,
  ConflictResolutionActionDescriptions,
  ConflictResolutionActionLabels,
  getAllowedResolutionActions,
} from "@/models/ConflictResolutionAction";
import { GroupResolveState, acceptNeedsAWinner } from "@/lib/conflictResolution";
import {
  ConflictClaimantStateColors,
  ConflictClaimantStateLabels,
  ConflictRuleCopy,
  ConflictRuleGroup,
  ConflictRuleOutcome,
  RuleCopyRole,
  RuleCopyRoleLabels,
} from "@/models/ConflictRule";
import { MigrationTypeLabels } from "@/models/Statistics/MigrationType";

interface EditableConflictGroupCardProps {
  readOnly?: false;
  group: ConflictGroupDto;
  /** Winner count + validity for the group under the current selections. */
  state: GroupResolveState;
  /** The action a file will resolve with (operator choice, prior resolution, or default). */
  actionFor: (file: ConflictGroupFileDto) => ConflictResolutionAction;
  /** Change one file's action (parent enforces the single-winner rule). */
  onFileAction: (file: ConflictGroupFileDto, action: ConflictResolutionAction) => void;
  /** Multi-file groups can collapse; a fully-resolved group starts collapsed. */
  collapsed: boolean;
  onToggleCollapse: (value: boolean) => void;
  submitting: boolean;
  confirming: boolean;
  onRequestResolve: () => void;
  onConfirmResolve: () => void;
  onCancelConfirm: () => void;
  disabled?: boolean;
  bulkBusy: boolean;
}

/**
 * A conflict rule's plan for one group, shown as the drawer shows the group, without anything to change: each copy
 * with where it stood, the part it plays, and the decision the rule records on it.
 */
interface ReadOnlyConflictGroupCardProps {
  readOnly: true;
  group: ConflictRuleGroup;
  collapsed: boolean;
  onToggleCollapse: (value: boolean) => void;
}

type ConflictGroupCardProps = EditableConflictGroupCardProps | ReadOnlyConflictGroupCardProps;

/** Distinct conflict reasons present in the group (usually one), for the header chips. */
const distinctReasons = (reasons: (ConflictReason | null | undefined)[]): ConflictReason[] => {
  const set = new Set<ConflictReason>();
  for (const reason of reasons) if (reason) set.add(reason);
  return Array.from(set);
};

const ReasonBadge: React.FC<{ reason: ConflictReason }> = ({ reason }) => {
  const ReasonIcon = ConflictReasonIcons[reason];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-help">
          <Badge variant={ConflictReasonColors[reason]}>
            {ReasonIcon && <ReasonIcon className="size-3" />}
            {ConflictReasonLabels[reason]}
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{ConflictReasonDescriptions[reason]}</TooltipContent>
    </Tooltip>
  );
};

/**
 * The frame both variants share: a single-copy group renders compactly with no target-path header, so a long backlog
 * stays dense; a multi-copy group gets a collapsible header naming the target path.
 */
const GroupFrame: React.FC<{
  targetPath: string;
  /** Shown under the target path, such as the case a rule's group belongs to. */
  context?: React.ReactNode;
  reasons: ConflictReason[];
  fileCount: number;
  collapsed: boolean;
  onToggleCollapse: (value: boolean) => void;
  rows: React.ReactNode;
  occupantLine?: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ targetPath, context, reasons, fileCount, collapsed, onToggleCollapse, rows, occupantLine, footer }) => {
  if (fileCount === 1) {
    return (
      <div className="rounded-md border bg-card">
        {context && <div className="border-b px-4 py-2 text-xs text-muted-foreground">{context}</div>}
        {rows}
        {occupantLine && <div className="border-t px-4 py-2">{occupantLine}</div>}
        {footer}
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-card">
      <div className="flex items-start justify-between gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => onToggleCollapse(!collapsed)}
          className="flex min-w-0 flex-1 items-start gap-2 text-left"
          aria-expanded={!collapsed}
        >
          <ChevronRight
            className={cn(
              "mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform",
              !collapsed && "rotate-90"
            )}
          />
          <div className="min-w-0 flex-1">
            <p className="break-all text-sm font-medium">{targetPath}</p>
            {context && <div className="mt-0.5 text-xs text-muted-foreground">{context}</div>}
            <div className="mt-1 flex flex-wrap items-center gap-1">
              {reasons.map((r) => {
                const ReasonIcon = ConflictReasonIcons[r];
                return (
                  <Badge key={r} variant={ConflictReasonColors[r]} className="gap-1">
                    {ReasonIcon && <ReasonIcon className="size-3" />}
                    {ConflictReasonLabels[r]}
                  </Badge>
                );
              })}
            </div>
          </div>
        </button>
        <Badge variant="neutral" className="shrink-0">
          {fileCount} files
        </Badge>
      </div>

      {!collapsed && (
        <>
          {occupantLine && <div className="border-t px-4 py-2">{occupantLine}</div>}
          <div className="divide-y border-t">{rows}</div>
          {footer}
        </>
      )}
    </div>
  );
};

/** The last segment of a source path, for the bold line of a copy that has no stored name of its own. */
const leafOf = (path: string): string => {
  const segments = path.split(/[\\/]/).filter((segment) => segment.length > 0);
  return segments.length > 0 ? segments[segments.length - 1] : path;
};

const PlannedGroupCard: React.FC<ReadOnlyConflictGroupCardProps> = ({ group, collapsed, onToggleCollapse }) => {
  const copyRow = (copy: ConflictRuleCopy) => {
    const winner = copy.waitsForFileId
      ? group.copies.find((candidate) => candidate.fileId === copy.waitsForFileId)
      : undefined;
    return (
      <div
        key={copy.fileId}
        className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="min-w-0 flex-1">
          <p className="break-all text-sm font-medium">{leafOf(copy.sourcePath)}</p>
          <p className="break-all text-xs text-muted-foreground">{copy.sourcePath}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {copy.state && (
              <Badge variant={ConflictClaimantStateColors[copy.state]}>{ConflictClaimantStateLabels[copy.state]}</Badge>
            )}
            {copy.reason && <ReasonBadge reason={copy.reason} />}
            {copy.role && copy.role !== RuleCopyRole.Untouched && (
              <Badge variant={copy.role === RuleCopyRole.Winner ? "outline-primary" : "neutral"}>
                {RuleCopyRoleLabels[copy.role]}
              </Badge>
            )}
          </div>
          {copy.waitsForFileId && (
            <p className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
              <Hourglass className="mt-0.5 size-3.5 shrink-0" />
              <span className="min-w-0 break-all">
                Will wait for {winner?.sourcePath ?? "the winner"} to upload
              </span>
            </p>
          )}
        </div>
        <div className="shrink-0 text-xs sm:w-56 sm:text-right">
          {copy.action ? (
            <span className={cn("font-medium", colorVariantClasses[ConflictResolutionActionColors[copy.action]]?.text)}>
              {ConflictResolutionActionLabels[copy.action]}
            </span>
          ) : (
            <span className="text-muted-foreground">No change</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <GroupFrame
      targetPath={group.targetPath}
      context={
        <>
          {MigrationTypeLabels[group.family]} · {group.parentLabel}
          {group.outcome === ConflictRuleOutcome.Skipped && group.label && (
            <span className="text-amber-600 dark:text-amber-400"> · Skipped: {group.label}</span>
          )}
        </>
      }
      reasons={distinctReasons(group.copies.map((copy) => copy.reason))}
      fileCount={group.copies.length}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      rows={group.copies.map(copyRow)}
    />
  );
};

/**
 * One conflict group. Editable in the resolve drawer: single-file groups (the common Office size/hash-mismatch case)
 * render as one compact card; multi-file groups render a collapsible card with a per-file list. Each file row shows
 * its local size and, when a SharePoint occupant is known, a local-vs-SharePoint size comparison. Read-only for a
 * conflict rule's preview and run trail.
 */
export const ConflictGroupCard: React.FC<ConflictGroupCardProps> = (props) =>
  props.readOnly ? <PlannedGroupCard {...props} /> : <EditableGroupCard {...props} />;

/** True when every file in the group has already been resolved/acknowledged (none still Unresolved). */
export const isGroupResolved = (group: ConflictGroupDto): boolean =>
  group.files.every(
    (f) => f.conflictStatus != null && f.conflictStatus !== MigrationFlagStatus.UnResolved
  );

const EditableGroupCard: React.FC<EditableConflictGroupCardProps> = ({
  group,
  state,
  actionFor,
  onFileAction,
  collapsed,
  onToggleCollapse,
  submitting,
  confirming,
  onRequestResolve,
  onConfirmResolve,
  onCancelConfirm,
  disabled,
  bulkBusy,
}) => {
  const { winners, missingWinner, invalid } = state;
  const isSingle = group.files.length === 1;

  const fileRow = (file: ConflictGroupFileDto) => {
    const current = actionFor(file);
    const allowed = getAllowedResolutionActions(file.conflictReason);
    const isResolved =
      file.conflictStatus != null && file.conflictStatus !== MigrationFlagStatus.UnResolved;
    const occ = group.occupantSizeInMegabytes;
    const delta = occ != null ? file.fileSizeInMegabytes - occ : null;
    return (
      <div
        key={file.fileId}
        className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="min-w-0 flex-1">
          <p className="break-all text-sm font-medium">{file.filename}</p>
          <p className="break-all text-xs text-muted-foreground">{file.filePath}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <MigrationStatusChip status={file.status} isFile />
            {file.conflictReason && <ReasonBadge reason={file.conflictReason} />}
            {isResolved && file.conflictStatus && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-help">
                    <Badge variant={ConflictStatusColors[file.conflictStatus]}>
                      {ConflictStatusLabels[file.conflictStatus]}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  {ConflictStatusDescriptions[file.conflictStatus]}
                </TooltipContent>
              </Tooltip>
            )}
            {file.conflictStatus === MigrationFlagStatus.Resolved && file.conflictResolution == null && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-help">
                    <Badge variant="neutral">Settled by upload</Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  The upload finished and closed this conflict on its own; no operator chose a resolution.
                </TooltipContent>
              </Tooltip>
            )}
            <span className="text-xs text-muted-foreground">
              {file.fileSizeInMegabytes.toFixed(1)} MB
              {occ != null && (
                <>
                  {" · SharePoint "}
                  {occ.toFixed(1)} MB
                  {delta != null && Math.abs(delta) >= 0.05 && (
                    <span
                      className={cn(
                        delta > 0
                          ? "text-amber-600 dark:text-amber-400"
                          : "text-muted-foreground"
                      )}
                    >
                      {" ("}
                      {delta > 0 ? "+" : "−"}
                      {Math.abs(delta).toFixed(1)}
                      {")"}
                    </span>
                  )}
                </>
              )}
            </span>
          </div>
          {file.waitingForState && (
            <p
              className={cn(
                "mt-1 flex items-start gap-1.5 text-xs",
                colorVariantClasses[ConflictWaitingForStateColors[file.waitingForState]].text
              )}
            >
              <Hourglass className="mt-0.5 size-3.5 shrink-0" />
              <span className="min-w-0 break-all">
                {conflictWaitingMessage(
                  file.waitingForState,
                  file.waitingForFilePath,
                  acceptNeedsAWinner(group, file)
                )}
              </span>
            </p>
          )}
        </div>
        <div className="shrink-0 sm:w-56">
          <Select
            value={current}
            onValueChange={(value) => onFileAction(file, value as ConflictResolutionAction)}
            disabled={disabled || submitting || bulkBusy}
          >
            <SelectTrigger
              size="sm"
              className={cn(
                "w-full",
                colorVariantClasses[ConflictResolutionActionColors[current]]?.text
              )}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {allowed.map((action) => (
                <ActionSelectItem
                  key={action}
                  value={action}
                  label={ConflictResolutionActionLabels[action]}
                  description={ConflictResolutionActionDescriptions[action]}
                  labelClassName={cn(
                    colorVariantClasses[ConflictResolutionActionColors[action]]?.text
                  )}
                />
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  };

  const footer = (
    <div className="flex items-center justify-between gap-2 border-t px-4 py-3">
      <p className="text-xs text-muted-foreground">
        {winners > 1 ? (
          <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
            <AlertTriangle className="size-3.5" />
            Select at most one winner (Replace from disk) per group.
          </span>
        ) : missingWinner ? (
          <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
            <AlertTriangle className="size-3.5" />
            {isSingle
              ? "Choose “Replace from disk” or “Rename & keep both” to place this file."
              : "Nominate one file as “Replace from disk” so the others can accept it."}
          </span>
        ) : winners === 0 ? (
          <>
            {isSingle
              ? "This file uses the action shown."
              : "No winner — each file uses the action shown."}
          </>
        ) : (
          <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="size-3.5" />
            1 winner will overwrite the destination.
          </span>
        )}
      </p>
      {confirming ? (
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs text-red-600 dark:text-red-400">
            Overwrite the SharePoint copy?
          </span>
          <Button size="sm" variant="outline" onClick={onCancelConfirm}>
            Cancel
          </Button>
          <Button size="sm" variant="destructive" onClick={onConfirmResolve}>
            Confirm overwrite
          </Button>
        </div>
      ) : (
        <Button
          size="sm"
          disabled={disabled || submitting || invalid || bulkBusy}
          onClick={onRequestResolve}
        >
          {submitting && <Loader2 className="size-4 animate-spin" />}
          Resolve group
        </Button>
      )}
    </div>
  );

  const occupantLine = group.occupantFilePath && (
    <div className="flex items-start gap-1.5 rounded-md bg-primary/5 px-2 py-1 text-xs">
      <Cloud className="mt-0.5 size-3.5 shrink-0 text-primary" />
      <span className="min-w-0 break-all">
        <span className="text-muted-foreground">Already in SharePoint (what “Accept” adopts): </span>
        <span className="font-medium">{group.occupantFilePath}</span>
        {group.occupantSizeInMegabytes != null && (
          <span className="text-muted-foreground">
            {" · "}
            {group.occupantSizeInMegabytes.toFixed(1)} MB
          </span>
        )}
      </span>
    </div>
  );

  return (
    <GroupFrame
      targetPath={group.targetPath}
      reasons={distinctReasons(group.files.map((file) => file.conflictReason))}
      fileCount={group.files.length}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      rows={group.files.map(fileRow)}
      occupantLine={occupantLine}
      footer={footer}
    />
  );
};

export default ConflictGroupCard;
